import FullPageLoader from "@Components/FullPageLoader";
import loadable from "@loadable/component";
import ErrorBoundary from "@Components/ErrorBoundary";
import loadData from "../data/loadData";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchBlogPageDataIfNeeded } from "@Pages/Blog/BlogPost/data/blogPageReducer";
import { getBlogLanguageFromPath } from "@Pages/Blog/utils/blogLanguage";
import isBlogRevampEnabled from "@Pages/Blog/utils/isBlogRevampEnabled";
import getBlogAllPostsData from "@Pages/Blog/BlogAllPosts/data/getBlogAllPostsData";
import getBlogCategoryData from "@Pages/Blog/BlogCategory/data/getBlogCategoryData";
import { STAGE_QUERY_PARAM } from "@Pages/Blog/BlogHomeRevamp/settings";

const BlogGrid = loadable(() => import("./BlogGrid"), {
  fallback: <FullPageLoader />,
});

const BlogAllPosts = loadable(() => import("../BlogAllPosts/BlogAllPosts"), {
  fallback: <FullPageLoader />,
});

const BlogCategory = loadable(() => import("../BlogCategory/BlogCategory"), {
  fallback: <FullPageLoader />,
});

const LATEST_PATH = /^\/blog\/latest\/?$/;
const CATEGORY_PATH = /^\/blog\/category\/([^/]+)\/?$/;

export default (props: any): JSX.Element => {
  const dispatch = useDispatch();
  const pathname: string = props?.location?.pathname ?? "";
  const isRevamp = isBlogRevampEnabled(getBlogLanguageFromPath(pathname));
  const showAllBlogs = LATEST_PATH.test(pathname) && isRevamp;
  const categorySlug = isRevamp ? pathname.match(CATEGORY_PATH)?.[1] : undefined;

  useEffect(() => {
    if (!__CLIENT__) return;

    if (showAllBlogs) {
      const stage = new URLSearchParams(window.location.search).get(STAGE_QUERY_PARAM);
      dispatch(getBlogAllPostsData({ stage }));
      return;
    }

    if (categorySlug) {
      dispatch(getBlogCategoryData({ slug: decodeURIComponent(categorySlug) }));
      return;
    }

    const path = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const isCategory = path.includes("/category/");
    const isTag = path.includes("/tag/");
    const isLatest = path.includes("/latest");
    const isSearch = path.includes("/search");
    const searchQuery = searchParams.get("query") || searchParams.get("q") || "";

    const lang = getBlogLanguageFromPath(path);

    dispatch(
      fetchBlogPageDataIfNeeded(
        props,
        isCategory,
        isTag,
        isSearch,
        searchQuery,
        isLatest,
        false,
        lang,
      ),
    );
  }, [
    dispatch,
    showAllBlogs,
    categorySlug,
    props?.match?.params?.slug,
    props?.match?.params?.lang,
    props?.location?.search,
  ]);

  return (
    <ErrorBoundary>
      {showAllBlogs && <BlogAllPosts {...props} />}
      {categorySlug && (
        <BlogCategory
          slug={decodeURIComponent(categorySlug)}
          staticContext={props?.staticContext}
        />
      )}
      {!showAllBlogs && !categorySlug && <BlogGrid {...props} />}
    </ErrorBoundary>
  );
};

export { BlogGrid as PreFetchBlogGrid };
export { loadData };
